/**
 * Reading a stored transcript into speaker turns. Two readers, two inputs.
 *
 * `parseTranscript` takes `transcriptText`, which every transcribed call has.
 * The model returns one block of text with "Name: line" per turn, so the parse
 * follows the shape of the input rather than guessing at it. What it adds is the
 * degenerate case: when EVERY line carries the same name, that name is not a
 * separation - it is one guess repeated, which is exactly what a call with no
 * usable speaker cue produces. Those labels are dropped.
 *
 * `readTimedTurns` takes `transcriptTurns`, which only SOME calls have, and is
 * the better reader when it answers: the turns are already separated and each
 * one knows the second it began, so the pane can send the player there instead
 * of leaving the operator to scroll and guess.
 *
 * The rule behind all of it is the same: a wrong speaker label is worse than
 * no label, because it changes who said what. An unattributed paragraph is only
 * less informative; a confidently mislabelled one is untrue.
 */

export interface TranscriptTurn {
  /** Speaker as the model named them, or null when nothing is claimed. */
  who: string | null;
  line: string;
}

export interface ParsedTranscript {
  turns: TranscriptTurn[];
  /** True only when the transcript really does separate two or more speakers. */
  attributed: boolean;
}

/** "Operaator: ..." - a capitalised name of a few words, then the line. */
const SPEAKER_RE = /^([\p{Lu}][\p{L} .'-]{1,40}?):\s*(.+)$/u;

export function parseTranscript(text: string): ParsedTranscript {
  const parsed: TranscriptTurn[] = text
    .split(/\n+/)
    .map((raw) => raw.trim())
    .filter(Boolean)
    .map((line) => {
      const m = SPEAKER_RE.exec(line);
      return m ? { who: m[1]!, line: m[2]! } : { who: null, line };
    });

  const attributed = speakerNames(parsed).length > 1;
  return {
    turns: attributed ? parsed : parsed.map((t) => ({ who: null, line: t.line })),
    attributed,
  };
}

/**
 * Would this text still separate its speakers if it were saved?
 *
 * Asked before an operator's correction lands, because the failure is silent
 * and does not look like a mistake anybody made. `parseTranscript` needs two or
 * more DISTINCT "Name:" prefixes; below that it drops every label on purpose
 * (one name repeated is a guess, not a separation). So an edit that removes one
 * side of the conversation, or renames both speakers to the same thing, turns
 * the whole pane into a single unattributed block - which reads as "the speaker
 * split failed", not as "somebody edited this".
 *
 * Only ever a warning. The operator may know better than the parser, and a
 * transcript that genuinely holds one voice - a voicemail - is a real thing to
 * store.
 */
export function keepsSpeakers(text: string): boolean {
  return parseTranscript(text).attributed;
}

/** Distinct speakers in the order they first appear. */
export function speakerNames(turns: TranscriptTurn[]): string[] {
  const seen: string[] = [];
  for (const turn of turns) {
    if (turn.who && !seen.includes(turn.who)) seen.push(turn.who);
  }
  return seen;
}

/**
 * One turn of the timed transcript, as `CallLog.transcriptTurns` stores it.
 *
 * A ROLE rather than a name, unlike `TranscriptTurn` above. `transcriptText`
 * carries whatever word the model wrote ("Operaator:"), which is a string in
 * one language that would have to be parsed back out before it could be shown
 * in another; this column stores the role and the browser translates it, the
 * same way it already does for the live co-pilot's turns.
 */
export interface TimedTurn {
  /** Seconds from the start of the recording. Never negative. */
  at: number;
  /** Null when the audio did not say. Drawn unattributed, never forced. */
  speaker: "operator" | "client" | null;
  text: string;
}

/**
 * Read `transcriptTurns` off a call, or null if it holds anything else.
 *
 * The browser mirror of `readStoredTurns` in the API's
 * `crm-calls/lib/transcript/turns.ts`, and strict for the same reason it is:
 * a Json column is whatever was last written to it, including by a version of
 * this code that no longer exists, and it arrives here as `unknown` because the
 * wire cannot promise otherwise.
 *
 * ⚠ ONE BAD ENTRY REJECTS THE WHOLE ARRAY rather than being skipped. The caller
 * falls back to `parseTranscript`, which reads the same conversation off the
 * text column - so the cost of refusing is a transcript with no timestamps,
 * while the cost of skipping would be a transcript with a turn silently missing
 * from the middle of it. The reader has a complete answer to fall back to, so
 * it can afford to be strict.
 *
 * ⚠ NULL IS THE COMMON CASE, not a fault. Only ElevenLabs Scribe times its
 * words; Gemini is still the default transcriber and writes null here on
 * purpose, and every call recorded before this column existed has nothing.
 */
export function readTimedTurns(raw: unknown): TimedTurn[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out: TimedTurn[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
    const { at, speaker, text } = entry as { at?: unknown; speaker?: unknown; text?: unknown };
    if (typeof at !== "number" || !Number.isFinite(at) || at < 0) return null;
    if (typeof text !== "string" || !text) return null;
    if (speaker !== "operator" && speaker !== "client" && speaker !== null) return null;
    out.push({ at, speaker, text });
  }
  return out;
}
